import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { colors } from '../../../../constants/colors';
import { fontFamily, fontSize } from '../../../../constants/text';
import {
  OPEN_NOW_TEXT,
  OPENING_SOON_TEXT,
  CLOSING_SOON_TEXT,
  CLOSED_NOW_TEXT,
  PERMANENTLY_CLOSED_TEXT,
  UNKNOWN_HOURS_TEXT,
} from '../../../../constants/clinicDetailPage';
import {
  isAlwaysOpen,
  isOpenNow,
  isOpenSoon,
  isOutOfBusiness,
  isClosingSoon,
  getTodayHours,
  hasKnownHours,
} from '../../../../core/util/location';
import styled, { css } from 'styled-components';
import { isEmpty } from '../../../../core/util/empty';
import moment from 'moment-timezone';
import DateTime, { YEAR_MONTH_DAY_DASHED } from '../../../DateTime';
import { isMidnightish } from '../../../../core/util/time';
import { safeGet } from '../../../../core/util/object';
import { smallerThanOrEqualTo } from '../../../../core/util/styledComponents';

const formatTime = time => {
  const timeObject = moment(time);
  if (timeObject.minute() > 0) {
    return timeObject.format('h:mma');
  }

  return timeObject.format('ha');
};

const todayTime = time => {
  if (isEmpty(time)) return null;

  return isMidnightish(time) ? '00:00:00' : time;
};

const getStatus = location => {
  const dateString = DateTime.format(moment.tz(), location.timeZone)(YEAR_MONTH_DAY_DASHED);
  const todayHours = getTodayHours(location);
  const todayOpeningTime = todayTime(todayHours.fromTime);
  const todayClosingTime = todayTime(todayHours.toTime);
  const hasUnknownHours = !hasKnownHours(location.hours);

  const openUntilText = () => {
    // if location is closed permanently or has unknown hours -
    // we don't show the openUntilText
    if (hasUnknownHours || isOutOfBusiness(location)) {
      return '';
    } else if (isAlwaysOpen(location)) {
      return '(Open 24 hours)';
    } else if (isOpenSoon(location)) {
      return `Opens ${formatTime(`${dateString} ${todayOpeningTime}`)}`;
    }

    return `Closes ${formatTime(`${dateString} ${todayClosingTime}`)}`;
  };

  if (isOutOfBusiness(location)) {
    return {
      color: colors.ladyInRed,
      shortenedText: PERMANENTLY_CLOSED_TEXT,
    };
  } else if (hasUnknownHours) {
    return {
      color: colors.lanaDelGrey,
      shortenedText: UNKNOWN_HOURS_TEXT,
    };
  } else if (isClosingSoon(location)) {
    return {
      color: colors.peaches,
      openUntilText: openUntilText(),
      shortenedText: CLOSING_SOON_TEXT,
    };
  } else if (isAlwaysOpen(location) || isOpenNow(location)) {
    return {
      color: colors.grannySmith,
      openUntilText: openUntilText(),
      shortenedText: OPEN_NOW_TEXT,
    };
  } else if (isOpenSoon(location)) {
    return {
      color: colors.grannySmith,
      openUntilText: openUntilText(),
      shortenedText: OPENING_SOON_TEXT,
    };
  }

  return {
    color: colors.blueMonday,
    shortenedText: CLOSED_NOW_TEXT,
  };
};

const OpenStatus = styled.div`
  font-family: ${fontFamily.bold};
  font-size: ${fontSize.mediumSmall};
  -webkit-font-smoothing: antialiased;
  line-height: 21px;
  ${props => css`
    color: ${props.color};
  `};
`;

const Root = styled.div`
  display: flex;
  align-items: center;
`;

const OpenUntilText = styled.div`
  color: ${colors.blueMonday};
  margin-left: 8px;
  font-family: ${fontFamily.book};
  font-size: ${fontSize.mediumSmall};

  ${smallerThanOrEqualTo.sm`
      font-size: ${fontSize.small};
   `};
`;

class ClinicStatus extends Component {
  static propTypes = {
    location: PropTypes.object.isRequired,
    shortened: PropTypes.bool,
    className: PropTypes.string,
  };

  shouldShowOpenUntilText = status =>
    !this.props.shortened && !isEmpty(safeGet(status)('openUntilText'));

  render() {
    if (isEmpty(this.props.location)) {
      return null;
    }

    const status = getStatus(this.props.location);

    return (
      <Root>
        <OpenStatus color={status.color} className={this.props.className}>
          {status.shortenedText}
        </OpenStatus>
        {this.shouldShowOpenUntilText(status) && (
          <OpenUntilText>{status.openUntilText}</OpenUntilText>
        )}
      </Root>
    );
  }
}

export default ClinicStatus;
